import {ElevationApp} from "../../ElevationApp";
import {joinCandidates, joinSticks, removeItem, splitByCandidates, splitPerimeterBy} from "../../scenes/frameGrid/BlockOps";
import {resetAnchors} from "../../scenes/frameGrid/Anchors";

// a non-structural change: reconcile the grid in place (a cell convert, a corner
// re-render) — order detection still rebuilds when cells were added/removed. Marks are
// re-assigned first so a cell that just became a lite draws its mark on this redraw.
export function emitChanged(app: ElevationApp) {
    app.frameSpec.assignLiteMarks();
    app.frameSpec.frameBlocksChanged.emit(false);
}

// a structural change (cross flip re-nests the block tree): force a full grid
// rebuild so the StickMaterials are recreated against the new structure.
export function emitRebuild(app: ElevationApp) {
    app.frameSpec.assignLiteMarks();
    app.frameSpec.frameBlocksChanged.emit(true);
}

// drop the selection and fall back to the workspace tab — used once an action removes
// the selected element, so the now-stale contextual tab disappears.
export function deselect(app: ElevationApp) {
    app.viewSettings.selection.emit([]);
    app.viewSettings.activeTab.emit("workspace");
}

export function remove(app: ElevationApp, id: string) {
    if(removeItem(app.frameSpec, id)) {
        emitChanged(app);
        deselect(app);
    } else {
        app.draw.showError("Can't remove this element");
    }
}

// wait for the user to click the stick to split `stickId` by (see splitPerimeterBy).
// The ribbon shows only the pick's tab until the pick or a cancel. With only one stick
// to split by, split there at once.
export function startSplitBy(app: ElevationApp, stickId: string) {
    // each part of a split stick covers less of the outline, so its anchors start over as
    // one auto section.
    const split = (byId: string) => {
        const newId = splitPerimeterBy(app.frameSpec, stickId, byId);
        if(newId) {
            resetAnchors(app.frameSpec, newId, [stickId]);
            resetAnchors(app.frameSpec, stickId, [stickId]);
        }
        return newId;
    };

    const candidates = splitByCandidates(app.frameSpec, stickId);
    if(candidates.length === 1) {
        finishPick(app, () => split(candidates[0]));
        return;
    }

    app.viewSettings.stickPick.emit({
        stickId,
        onPick: byId => finishPick(app, () => split(byId)),
        title: "Split By",
        instructions: "Click a stick that meets the selected stick. The selected stick splits where they meet. A click on any other stick cancels.",
    });
}

// wait for the user to click the stick to join into `stickId` (see joinSticks). With
// only one stick to join, join it at once.
export function startJoin(app: ElevationApp, stickId: string) {
    // the joined stick covers more of the outline, so its anchors start over as one auto
    // section.
    const join = (otherId: string) => {
        const rootId = joinSticks(app.frameSpec, stickId, otherId);
        if(rootId) resetAnchors(app.frameSpec, rootId, [stickId, otherId]);
        return rootId;
    };

    const candidates = joinCandidates(app.frameSpec, stickId);
    if(candidates.length === 1) {
        finishPick(app, () => join(candidates[0]));
        return;
    }

    app.viewSettings.stickPick.emit({
        stickId,
        onPick: otherId => finishPick(app, () => join(otherId)),
        title: "Join",
        instructions: "Click a stick that was split from the selected stick. It joins into the selected stick. A click on any other stick cancels.",
    });
}

// leave a stick pick without a change.
export function cancelPick(app: ElevationApp) {
    app.viewSettings.stickPick.emit(null);
}

// end the pick and run its edit, then select the stick it returns. An edit that
// returns null (the picked stick doesn't fit) just ends the pick.
function finishPick(app: ElevationApp, edit: () => string | null) {
    const vs = app.viewSettings;
    const orientation = vs.selection.value.find(s => s.type === "stick");
    vs.stickPick.emit(null);

    const newId = edit();
    if(!newId) {
        // the tap that picked lit up the clicked stick; re-send the selection so every
        // stick's highlight matches it again.
        vs.selection.emit(vs.selection.value);
        return;
    }

    emitRebuild(app);
    if(orientation?.type === "stick") {
        vs.selection.emit([{type: "stick", id: newId, orientation: orientation.orientation}]);
        vs.activeTab.emit("selected");
    }
}
